
import { Link, useLocation } from "react-router-dom";
import { Home, MessageSquare, Bell, Search, UserCircle, Film } from "lucide-react";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Badge } from "@/components/ui/badge";

interface MobileNavBarProps {
  user: any;
}

export const MobileNavBar = ({ user }: MobileNavBarProps) => {
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  
  useEffect(() => {
    if (!user) return;

    // Initial fetch of unread notifications count
    const fetchUnreadCount = async () => {
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (!error && count !== null) {
        setUnreadCount(count);
      }
    };

    fetchUnreadCount();

    // Subscribe to new notifications
    const channel = supabase
      .channel('mobile-nav-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          setUnreadCount(prev => prev + 1);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          if (payload.new.is_read && !payload.old.is_read) {
            setUnreadCount(prev => Math.max(0, prev - 1));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const mobileNavItems = [
    { path: "/feed", icon: Home, label: "Feed" },
    { path: "/inbox", icon: MessageSquare, label: "Inbox" },
    { 
      path: "/notifications", 
      icon: Bell, 
      label: "Notifications",
      badge: unreadCount > 0 ? unreadCount : undefined
    },
    { path: "/quix", icon: Film, label: "Quix" },
    { path: "/profile", icon: UserCircle, label: "Profile" },
  ];

  if (!user) return null;

  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 h-16 bg-background/95 backdrop-blur-xl border-t border-border z-50">
      <div className="flex items-center justify-around h-full px-4">
        {mobileNavItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={cn(
              "flex flex-col items-center justify-center space-y-1 text-xs font-['SF_Pro_Display']",
              location.pathname === item.path
                ? "text-primary"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <div className="relative">
              <item.icon className="w-5 h-5" />
              {item.badge && (
                <Badge 
                  variant="destructive" 
                  className="absolute -top-2 -right-2 h-4 w-4 flex items-center justify-center p-0 rounded-full text-[10px]"
                >
                  {item.badge}
                </Badge>
              )}
            </div>
            <span>{item.label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
};
